<template>
    <div
        v-if="visible"
        class="fixed bottom-24 left-4 right-4 z-40 mx-auto max-w-lg rounded border border-brand-primary-200 bg-white shadow-lg dark:border-brand-primary-700 dark:bg-neutral-900 md:bottom-6 md:left-auto md:right-6"
    >
        <div class="p-4">
            <div class="mb-3 flex items-start justify-between gap-3">
                <div>
                    <div class="text-base font-semibold text-neutral-900 dark:text-white">
                        {{ title }}
                    </div>
                    <div v-if="!submitted" class="mt-1 text-sm leading-5 text-neutral-600 dark:text-neutral-300">
                        {{ copy.description }}
                    </div>
                </div>
                <button
                    class="flex h-8 w-8 shrink-0 items-center justify-center rounded text-xl leading-none text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-white"
                    type="button"
                    aria-label="Dismiss survey prompt"
                    @click="handleDismiss"
                >
                    &times;
                </button>
            </div>

            <div v-if="!submitted" class="space-y-4">
                <div class="grid grid-cols-5 gap-2">
                    <button
                        v-for="value in ratings"
                        :key="value"
                        class="flex h-12 items-center justify-center rounded border text-base font-bold"
                        :class="rating === value
                            ? 'border-brand-primary-600 bg-brand-primary-600 text-white'
                            : 'border-neutral-300 text-neutral-700 hover:border-brand-primary-500 hover:text-brand-primary-600 dark:border-neutral-700 dark:text-neutral-200'"
                        type="button"
                        :aria-label="`Rate V3ME ${value} out of 5`"
                        @click="handleRatingSelect(value)"
                    >
                        {{ value }}
                    </button>
                </div>
                <div class="flex justify-between text-xs text-neutral-500 dark:text-neutral-400">
                    <span>Needs work</span>
                    <span>Love it</span>
                </div>

                <div v-if="feedbackRequired">
                    <label class="mb-2 block text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                        {{ copy.feedback_prompt }}
                    </label>
                    <textarea
                        v-model="feedback"
                        class="min-h-[104px] w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 outline-none focus:border-brand-primary-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-white"
                        maxlength="3000"
                        :placeholder="copy.feedback_placeholder"
                        @focus="handleFeedbackStart"
                    ></textarea>
                </div>

                <div v-if="error" class="text-sm text-red-600 dark:text-red-400">
                    {{ error }}
                </div>

                <div class="flex items-center justify-end gap-2">
                    <button
                        class="rounded px-3 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
                        type="button"
                        :disabled="submitting"
                        @click="handleDismiss"
                    >
                        {{ copy.dismiss }}
                    </button>
                    <button
                        v-if="feedbackRequired"
                        class="rounded bg-brand-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                        type="button"
                        :disabled="submitting || !canSubmitFeedback"
                        @click="handleSubmit"
                    >
                        {{ submitting ? 'Sending...' : copy.submit }}
                    </button>
                </div>
            </div>

            <div v-else class="space-y-4">
                <div class="rounded bg-neutral-100 p-3 text-sm leading-5 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                    {{ submittedMessage }}
                </div>
                <div v-if="trustpilotFollowUpQueued" class="flex justify-start">
                    <TrustPilotStars />
                </div>
                <div class="flex justify-end">
                    <button
                        class="rounded bg-brand-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-primary-700"
                        type="button"
                        @click="visible = false"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mapActions} from 'vuex';
import TrustPilotStars from '@/components/TrustPilotStars.vue';
import {
  capturePostHogEvent,
  getPostHogDistinctId,
} from '@/utils/posthog';

const defaultPromptLocation = 'workout_saved';

export default {
  name: 'SurveyPrompt',
  components: {
    TrustPilotStars,
  },
  data() {
    return {
      copy: {
        title: 'How is V3ME working for you?',
        description: 'Rate your experience from 1 to 5. It only takes a second and helps us make V3ME better.',
        feedback_prompt: 'What could we improve?',
        feedback_placeholder: 'Tell us what is getting in your way, what feels confusing, or what would make V3ME more useful.',
        trustpilot_title: 'Thank you!',
        trustpilot_description: 'I am glad V3ME is helping. We will send a Trustpilot follow-up, and you can also leave a review now.',
        dismiss: 'Not now',
        submit: 'Send feedback',
      },
      eligible: false,
      error: null,
      feedback: '',
      feedbackStarted: false,
      loaded: false,
      manual: false,
      promptLocation: defaultPromptLocation,
      rating: null,
      ratings: [1, 2, 3, 4, 5],
      submitted: false,
      submitting: false,
      trustpilotFollowUpQueued: false,
      userContext: {},
      visible: false,
    };
  },
  computed: {
    authenticated() {
      return this.$store.state.auth.authenticated;
    },
    canSubmitFeedback() {
      return this.feedback.trim().length >= 5;
    },
    feedbackRequired() {
      return this.rating !== null && this.rating <= 3;
    },
    submittedMessage() {
      if (this.trustpilotFollowUpQueued) {
        return this.copy.trustpilot_description;
      }

      return 'Thank you. Your feedback is saved with your account so we can understand what to improve and follow up when it matters.';
    },
    title() {
      if (this.submitted) {
        return this.copy.trustpilot_title;
      }

      return this.copy.title;
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  methods: {
    ...mapActions({
      dismissPrompt: 'survey/dismiss',
      prompt: 'survey/prompt',
      submitSurvey: 'survey/submit',
      viewedPrompt: 'survey/viewed',
    }),
    eventProperties(extra = {}) {
      return {
        prompt_location: this.promptLocation,
        prompt_manual: this.manual,
        user_completed_workout_count: this.userContext.completed_workout_count ?? this.user?.workoutCount ?? null,
        ...extra,
      };
    },
    resetPromptState() {
      this.error = null;
      this.feedback = '';
      this.feedbackStarted = false;
      this.rating = null;
      this.submitted = false;
      this.submitting = false;
      this.trustpilotFollowUpQueued = false;
    },
    async loadPrompt(manual = false, force = false) {
      if ((this.loaded && !manual && !force) || !this.authenticated || !this.user || this.user.isAdmin) {
        return;
      }

      this.loaded = true;
      this.manual = manual;
      this.resetPromptState();

      try {
        const response = await this.prompt({
          params: {
            manual,
          },
        });
        const data = response?.data?.data;

        this.eligible = Boolean(data?.eligible);
        this.copy = {
          ...this.copy,
          ...(data?.copy || {}),
        };
        this.userContext = data?.user_context || {};
        this.visible = this.eligible;

        if (this.visible) {
          capturePostHogEvent('survey_prompt_viewed', this.eventProperties());

          try {
            await this.viewedPrompt({
              manual,
              promptLocation: this.promptLocation,
              metadata: {
                completed_workout_count: this.userContext.completed_workout_count ?? null,
              },
            });
          } catch (error) {
            // PostHog still captures the client-side view; monthly server throttling can miss this one view if the network drops.
          }
        }
      } catch (error) {
        this.visible = false;
      }
    },
    async handleDismiss() {
      this.visible = false;
      capturePostHogEvent('survey_prompt_dismissed', this.eventProperties({
        rating: this.rating,
      }));

      try {
        await this.dismissPrompt({
          promptLocation: this.promptLocation,
          metadata: {
            rating: this.rating,
          },
        });
      } catch (error) {
        // Dismiss locally even if the network drops; PostHog still captures production behavior.
      }
    },
    handleFeedbackStart() {
      if (this.feedbackStarted) {
        return;
      }

      this.feedbackStarted = true;
      capturePostHogEvent('survey_feedback_started', this.eventProperties({
        rating: this.rating,
      }));
    },
    async handleRatingSelect(value) {
      this.rating = value;
      this.error = null;

      capturePostHogEvent('survey_rating_selected', this.eventProperties({
        rating: value,
      }));

      if (value >= 4) {
        await this.handleSubmit();
      }
    },
    async handleSubmit() {
      if (this.submitting || this.rating === null) {
        return;
      }

      if (this.feedbackRequired && !this.canSubmitFeedback) {
        this.error = 'A little detail helps us understand what to improve.';
        return;
      }

      this.error = null;
      this.submitting = true;

      try {
        const response = await this.submitSurvey({
          feedback: this.feedbackRequired ? this.feedback.trim() : null,
          metadata: {
            completed_workout_count: this.userContext.completed_workout_count ?? null,
            prompt_manual: this.manual,
          },
          posthogDistinctId: getPostHogDistinctId(),
          promptLocation: this.promptLocation,
          rating: this.rating,
        });
        const data = response?.data?.data;

        this.trustpilotFollowUpQueued = Boolean(data?.trustpilot_follow_up_queued);
        this.submitted = true;

        capturePostHogEvent('survey_response_submitted', this.eventProperties({
          feedback_provided: this.feedbackRequired,
          rating: this.rating,
          survey_response_id: data?.survey_response_id ?? null,
          trustpilot_follow_up_queued: this.trustpilotFollowUpQueued,
        }));

        if (this.trustpilotFollowUpQueued) {
          capturePostHogEvent('survey_trustpilot_cta_shown', this.eventProperties({
            rating: this.rating,
          }));
        }
      } catch (error) {
        this.error = 'We could not save that feedback. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
    handlePromptEvent(event) {
      this.promptLocation = event?.detail?.promptLocation || defaultPromptLocation;
      this.loadPrompt(Boolean(event?.detail?.manual), true);
    },
  },
  mounted() {
    window.addEventListener('v3me:survey-prompt', this.handlePromptEvent);
  },
  beforeUnmount() {
    window.removeEventListener('v3me:survey-prompt', this.handlePromptEvent);
  },
  watch: {
    authenticated() {
      this.loaded = false;
    },
    user() {
      this.loaded = false;
    },
  },
};
</script>
